import { useEffect, useRef } from 'react';
import {
  createChart,
  createSeriesMarkers,
  AreaSeries,
  ColorType,
  LineStyle,
  LineType,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { PriceHistoryPointDto } from '@/lib/models';
import type { SnakeTradeDot } from '@/lib/snake-market';
import { cssVar } from '@/lib/theme/cssVar';

export interface SnakePriceChartProps {
  /** Up-probability OHLC history for the current series instance. */
  history: PriceHistoryPointDto[];
  /** Latest live Up% (0–100) — drawn as a horizontal current-price line. `null` hides it. */
  currentUpPercent: number | null;
  /**
   * Whether the series has a strike "price to beat". Snake markets resolve at
   * the strike, where Up% = 50, so the target line sits at 50 when a strike
   * exists. (We render in Up% space, not dollar space — the series y-axis is
   * the contract's Up probability.)
   */
  hasStrike: boolean;
  /** Streamed trade dots (mapped onto the Up% series). */
  tradeDots: SnakeTradeDot[];
}

const UP_COLOR = cssVar('--color-success');
// Strike line: primary at 55% alpha. No exact token (--muted is 0.5); kept as a
// primary-derived alpha ramp.
const STRIKE_COLOR = 'rgba(54, 0, 82, 0.55)';
const CURRENT_COLOR = cssVar('--color-accent');
const UP_DOT = cssVar('--color-success-dark');
const DOWN_DOT = cssVar('--color-danger-dark');

/** Strike sits at Up% = 50 (the resolution boundary in probability space). */
const STRIKE_UP_PERCENT = 50;

/**
 * Convert an Up-probability OHLC history to chart points: `closePrice` (0–1) →
 * Up% (0–100), keyed by the period-end timestamp. Skips points with no close.
 */
function toSeries(history: PriceHistoryPointDto[]): { time: UTCTimestamp; value: number }[] {
  return history
    .filter((p) => p.closePrice != null)
    .map((p) => ({
      time: Math.floor(Number(p.endPeriodTs)) as UTCTimestamp,
      value: Math.round((p.closePrice as number) * 100),
    }))
    .sort((a, b) => (a.time as number) - (b.time as number));
}

/**
 * Snake market price chart (ARC-84 §5) — reuses TradingView Lightweight Charts.
 *
 * Draws the Up-probability line (shaded area) for the current series instance,
 * a dashed **target/strike** reference line (at Up% = 50, the resolution
 * boundary), a dashed **current-price** line at the live Up%, and **trade dots**
 * streamed via Centrifugo (green = Up hit, red = Down hit). Static history +
 * live overlays; follows the binary `MarketPriceChart` pattern.
 */
export function SnakePriceChart({ history, currentUpPercent, hasStrike, tradeDots }: SnakePriceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const areaRef = useRef<ISeriesApi<'Area'> | null>(null);
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const priceLinesRef = useRef<IPriceLine[]>([]);

  // Build the chart once; data updates happen in dependent effects below so the
  // chart isn't torn down on every live tick.
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: cssVar('--color-muted'),
        fontFamily: "'Outfit', sans-serif",
        fontSize: 10,
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: cssVar('--color-border'), style: LineStyle.Dashed },
      },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false, rightOffset: 4 },
      handleScroll: false,
      handleScale: false,
      crosshair: { vertLine: { visible: false }, horzLine: { visible: false } },
    });

    const area = chart.addSeries(AreaSeries, {
      lineColor: UP_COLOR,
      lineWidth: 2,
      lineType: LineType.Curved,
      topColor: 'rgba(46, 204, 113, 0.28)',
      bottomColor: 'rgba(46, 204, 113, 0)',
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    chartRef.current = chart;
    areaRef.current = area;
    markersRef.current = createSeriesMarkers(area, []);

    return () => {
      chart.remove();
      chartRef.current = null;
      areaRef.current = null;
      markersRef.current = null;
      priceLinesRef.current = [];
    };
  }, []);

  // Up% history + strike/current reference price-lines.
  useEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    area.setData(toSeries(history));

    // Clear stale price lines on series/strike/price changes.
    priceLinesRef.current.forEach((l) => area.removePriceLine(l));
    const next: IPriceLine[] = [];

    if (hasStrike) {
      next.push(
        area.createPriceLine({
          price: STRIKE_UP_PERCENT,
          color: STRIKE_COLOR,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: 'Target',
        }),
      );
    }
    if (currentUpPercent != null) {
      next.push(
        area.createPriceLine({
          price: currentUpPercent,
          color: CURRENT_COLOR,
          lineWidth: 1,
          lineStyle: LineStyle.Solid,
          axisLabelVisible: true,
          title: 'Up%',
        }),
      );
    }
    priceLinesRef.current = next;

    chartRef.current?.timeScale().fitContent();
  }, [history, hasStrike, currentUpPercent]);

  // Trade-dot markers on the Up% series.
  useEffect(() => {
    const markers: SeriesMarker<Time>[] = tradeDots.map((d) => ({
      time: d.time as UTCTimestamp,
      position: d.side === 'up' ? 'aboveBar' : 'belowBar',
      color: d.side === 'up' ? UP_DOT : DOWN_DOT,
      shape: 'circle',
    }));
    markersRef.current?.setMarkers(markers);
  }, [tradeDots]);

  return <div ref={containerRef} className="absolute inset-0" />;
}
